import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import {
  HoverCard, HoverCardTrigger, HoverCardContent,
} from "../ui/hover-card";
import { EntityImageFallback } from "../EntityImageFallback";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cache = new Map();

const KIND_LABEL = {
  person: "Person", organization: "Organization", operation: "Operation",
  government_agency: "Government Agency", intelligence_service: "Intelligence Service",
  company: "Company", network: "Network", program: "Program", event: "Event",
  place: "Location", document: "Document", technology: "Technology",
};

const Row = ({ label, value }) =>
  value ? (
    <div className="flex gap-2 items-baseline">
      <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#7B7168] shrink-0 w-[92px]">
        {label}
      </span>
      <span className="font-mono text-[11px] text-[#C9C3B5]">{value}</span>
    </div>
  ) : null;

const imgSrc = (u) => (/^https?:\/\//i.test(u) ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

/**
 * Desktop hover context card for linked entities. Canonical data only —
 * same profile record (portrait, type, status) as the full Intelligence
 * Reference page. Failures are never cached so transient errors recover.
 */
export const EntityHoverCard = ({ slug, whyConnected, children }) => {
  const [data, setData] = useState(cache.get(slug) || null);

  const load = () => {
    if (cache.has(slug)) { setData(cache.get(slug)); return; }
    axios.get(`${API}/hover/entity/${slug}`)
      .then((r) => { cache.set(slug, r.data); setData(r.data); })
      .catch(() => setData({ found: false }));
  };

  return (
    <HoverCard openDelay={250} closeDelay={80} onOpenChange={(o) => o && load()}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="start"
        className="w-[360px] border rounded-none p-0 shadow-2xl hidden md:block"
        style={{ borderColor: "#3A3630", background: "#12110E" }}
        data-testid={`entity-hover-card-${slug}`}
      >
        {!data ? (
          <div className="p-4 font-mono text-[9px] uppercase tracking-[0.3em] text-[#7B7168]">
            // Retrieving file…
          </div>
        ) : !data.found ? (
          <div className="p-4 font-mono text-[9px] uppercase tracking-[0.3em] text-[#7B7168]">
            // No file on record
          </div>
        ) : (
          <div className="p-4">
            <div className="flex gap-3">
              {data.portrait_url ? (
                <img
                  src={imgSrc(data.portrait_url)}
                  alt={data.name}
                  className="w-16 h-20 object-cover object-top shrink-0"
                  style={{ filter: "sepia(0.15)", border: "1px solid #2D2A26" }}
                  data-testid={`hover-portrait-${slug}`}
                />
              ) : (
                <EntityImageFallback kind={data.kind || data.kind_group}
                                     className="w-16 h-20 shrink-0" compact />
              )}
              <div className="min-w-0">
                <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#A68F5E]">
                  {data.entity_type || KIND_LABEL[data.kind] || KIND_LABEL[data.kind_group] || "Entity"}
                </div>
                <div className="font-display text-[18px] text-[#E4DFD3] mt-1 leading-snug">
                  {data.name}
                </div>
                {data.has_profile && (
                  <div className="font-mono text-[7.5px] uppercase tracking-[0.25em] text-[#3FB950] mt-1">
                    Intelligence reference on file
                  </div>
                )}
              </div>
            </div>
            {(data.historical_reference || data.archive_summary) && (
              <p className="text-[12px] text-[#A39E93] leading-relaxed mt-3 border-l-2 pl-3"
                 style={{ borderColor: "#A68F5E" }}>
                {data.historical_reference || data.archive_summary}
              </p>
            )}
            <div className="mt-3 space-y-1.5">
              <Row label="Record span" value={data.active_years} />
              <Row label="Appearances" value={`${data.appearances} dossier${data.appearances === 1 ? "" : "s"}`} />
              <Row label="Connections" value={data.connections || null} />
              <Row label="Why connected" value={whyConnected} />
            </div>
            <div className="mt-3 pt-3 border-t flex items-center justify-between" style={{ borderColor: "#2D2A26" }}>
              {!data.has_profile ? (
                <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#5A554C]">
                  Network entity — basic file
                </span>
              ) : <span />}
              <Link
                to={data.url || "#"}
                className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.25em] text-[#D92D20] hover:underline"
                data-testid={`hover-open-file-${slug}`}
              >
                Open file <ArrowUpRight size={10} />
              </Link>
            </div>
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};
